import { useUnit } from 'effector-react';
import { Trash2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForbiddenUserAction } from 'entities/viewer';
import { User } from 'shared/api';
import {
	ConfirmModalProps,
	createConfirmModal,
	ModalFormActionIconProps,
	ModalFormButtonProps,
} from 'shared/ui';
import { DeleteUserModel } from '../../model';

export const createDeleteUserModal = (model: DeleteUserModel) => {
	const ConfirmModal = createConfirmModal(model);

	const DeleteUserModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [user] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('delete-user.title')}
				cancelButtonProps={{
					children: t('delete-user.cancel'),
				}}
				submitButtonProps={{
					children: t('delete-user.delete'),
					variant: 'destructive',
				}}
				{...props}
			>
				{t('delete-user.description', { name: user?.name })}
			</ConfirmModal>
		);
	});

	const DeleteUserModalButton = forwardRef<HTMLButtonElement, ModalFormButtonProps<User>>(
		({ modalData, ...props }, ref) => {
			const { t } = useTranslation();

			const disabled = useForbiddenUserAction(modalData);

			return (
				<ConfirmModal.Button
					ref={ref}
					data-test-id='delete-user-button'
					variant='destructive'
					leftSection={<Trash2 />}
					disabled={disabled}
					modalData={modalData}
					{...props}
				>
					{t('delete-user.delete')}
				</ConfirmModal.Button>
			);
		},
	);

	const DeleteUserModalActionIcon = forwardRef<HTMLButtonElement, ModalFormActionIconProps<User>>(
		({ modalData, ...props }, ref) => {
			const disabled = useForbiddenUserAction(modalData);

			return (
				<ConfirmModal.ActionIcon
					ref={ref}
					data-test-id='delete-user-action-icon'
					variant='ghost-destructive'
					disabled={disabled}
					modalData={modalData}
					{...props}
				>
					<Trash2 />
				</ConfirmModal.ActionIcon>
			);
		},
	);

	return Object.assign(DeleteUserModal, {
		Button: DeleteUserModalButton,
		ActionIcon: DeleteUserModalActionIcon,
	});
};
